import css from "./QuickCreatePage.sass?inline"
import {createElement, PageContext, PageFactory} from "@opendaw/lib-jsx"
import {StudioService} from "@/service/StudioService"
import {Html} from "@opendaw/lib-dom"
import {Option, UUID} from "@opendaw/lib-std"
import {Promises} from "@opendaw/lib-runtime"
import {
    AudioContentFactory,
    Project,
    ProjectMeta,
    ProjectProfile
} from "@opendaw/studio-core"
import {InstrumentFactories} from "@opendaw/studio-adapters"
import {AudioFileBox} from "@opendaw/studio-boxes"
import {generateSong, separateStems, fetchAudioAsArrayBuffer, MUSIC_MODELS} from "@/ui/ai-copilot/AIService"
import {getCreditsInfo, useCredit, totalRemaining, onCreditsChange, trackAnalyticsApiCall} from "@/ui/credits/CreditStore"
import {showCheckoutModal} from "@/ui/credits/CheckoutModal"
import {saveTrack, updateTrack, deleteTrack} from "@/ui/pages/TrackStore"
import {addFlashlightEffect} from "@/ui/pages/FlashlightEffect"

const className = Html.adoptStyleSheet(css, "QuickCreatePage")

const SUGGESTIONS = [
    "Dark trap beat with 808 bass and hi-hats",
    "Chill lo-fi hip hop with piano and vinyl crackle",
    "High energy EDM drop with supersaw leads",
    "Drill beat with sliding 808s and dark melodies",
    "Ambient phonk with cowbells and reverb",
    "Future bass with bright plucks and vocal chops"
]

export const QuickCreatePage: PageFactory<StudioService> = ({service}: PageContext<StudioService>) => {
    const input: HTMLInputElement = (
        <input type="text"
               className="main-input"
               placeholder="Describe your song… e.g. 'Dark trap beat with 808 bass and haunting melodies'"/>
    ) as unknown as HTMLInputElement

    const modelSelect: HTMLSelectElement = (
        <select className="model-select">
            {MUSIC_MODELS.map(m => <option value={m.id}>{m.name}</option>)}
        </select>
    ) as unknown as HTMLSelectElement

    const shareCheck: HTMLInputElement = (
        <input type="checkbox" checked/>
    ) as unknown as HTMLInputElement

    const creditsLabel: HTMLSpanElement = <span className="credits-label"/> as unknown as HTMLSpanElement
    const generateBtn: HTMLButtonElement = (
        <button className="generate-btn">Generate →</button>
    ) as unknown as HTMLButtonElement

    function updateCredits(): void {
        const info = getCreditsInfo()
        if (info.unlimited) {
            creditsLabel.textContent = "Unlimited"
        } else {
            const total = info.daily + info.purchased
            const limit = info.dailyLimit
            creditsLabel.textContent = `${total}/${limit} free today`
        }
        const rem = totalRemaining()
        generateBtn.disabled = rem <= 0
        if (rem <= 0) {
            generateBtn.textContent = "Get Credits →"
            generateBtn.onclick = () => showCheckoutModal()
        } else {
            generateBtn.textContent = "Generate →"
            generateBtn.onclick = doGenerate
        }
    }
    updateCredits()
    onCreditsChange(updateCredits)

    async function importAudioToProject(project: Project, name: string, arrayBuffer: ArrayBuffer): Promise<void> {
        const {status, value: sample, error} = await Promises.tryCatch(
            service.sampleService.importFile({name, arrayBuffer})
        )
        if (status === "rejected") {
            console.warn(`Failed to import '${name}'`, error)
            return
        }
        const uuid = UUID.parse(sample.uuid)
        await Promises.tryCatch(service.sampleManager.getAudioData(uuid))
        project.editing.modify(() => {
            const {trackBox, instrumentBox} = project.api.createInstrument(InstrumentFactories.Tape)
            instrumentBox.label.setValue(name)
            const audioFileBox = project.boxGraph.findBox<AudioFileBox>(uuid)
                .unwrapOrElse(() => AudioFileBox.create(project.boxGraph, uuid, box => {
                    box.fileName.setValue(name)
                    box.startInSeconds.setValue(0)
                    box.endInSeconds.setValue(sample.duration)
                }))
            AudioContentFactory.createNotStretchedRegion({
                boxGraph: project.boxGraph, sample, audioFileBox, position: 0, targetTrack: trackBox
            })
        })
    }

    async function doGenerate(): Promise<void> {
        const prompt = input.value.trim()
        if (!prompt) { input.focus(); return }
        if (totalRemaining() <= 0) { showCheckoutModal(); return }

        useCredit()
        updateCredits()
        generateBtn.disabled = true
        generateBtn.textContent = "Creating…"

        const trackId = UUID.generate().toString()
        const modelId = modelSelect.value
        const songName = prompt.length > 40 ? prompt.substring(0, 40) + "…" : prompt

        saveTrack({
            id: trackId,
            title: songName,
            description: prompt,
            model: modelId,
            duration: 0,
            bpm: 120,
            instrumental: true,
            shared: shareCheck.checked,
            audioUrl: "",
            createdAt: Date.now(),
            plays: 0,
            likes: 0,
            loading: true
        })

        import("@opendaw/lib-jsx").then(({RouteLocation}) => {
            RouteLocation.get().navigateTo("/library")
        })

        input.value = ""
        generateBtn.disabled = false
        generateBtn.textContent = "Generate →"
        updateCredits()

        try {
            trackAnalyticsApiCall("/api/replicate/predictions", "replicate-musicgen")
            const audioUrl = await generateSong(modelId, prompt, 30, 120, true)
            const stems = await separateStems(audioUrl)

            const project = Project.new(service)
            service.projectProfileService.setValue(
                Option.wrap(new ProjectProfile(UUID.generate(), project, ProjectMeta.init(songName), Option.None))
            )

            const stemEntries: [string, string][] = [
                ["Vocals", stems.vocals],
                ["Drums", stems.drums],
                ["Bass", stems.bass],
                ["Other", stems.other]
            ]
            for (const [name, url] of stemEntries) {
                try {
                    const buffer = await fetchAudioAsArrayBuffer(url)
                    await importAudioToProject(project, name, buffer)
                } catch (e) {
                    console.warn(`Failed to import ${name}:`, e)
                }
            }

            updateTrack(trackId, {audioUrl, duration: 30, loading: false})
        } catch (e: any) {
            console.error("Quick Create error:", e)
            deleteTrack(trackId)
        }
    }

    generateBtn.onclick = doGenerate
    input.addEventListener("keydown", (event) => { if (event.key === "Enter") doGenerate() })

    const suggestionPills: HTMLDivElement = <div className="suggestions"/> as unknown as HTMLDivElement
    for (const s of SUGGESTIONS) {
        const pill: HTMLButtonElement = <button className="pill">{s}</button> as unknown as HTMLButtonElement
        pill.onclick = () => { input.value = s; input.focus() }
        suggestionPills.appendChild(pill)
    }

    const root: HTMLDivElement = (
        <div className={className}>
            <div className="hero">
                <h1>What music will you <span className="highlight">create</span> today?</h1>
                <p className="sub">Create stunning music by describing it with text.</p>
                <div className="input-wrap">
                    {input}
                    <div className="controls-bar">
                        <div className="model-wrap">
                            {modelSelect}
                        </div>
                        <label className="share-label">
                            {shareCheck}
                            <span>Share</span>
                        </label>
                        {creditsLabel}
                        {generateBtn}
                    </div>
                </div>
                <div className="or-try">
                    <span className="or-try-label">or try</span>
                    {suggestionPills}
                </div>
            </div>
        </div>
    ) as unknown as HTMLDivElement
    addFlashlightEffect(root)
    return root
}
